// The Insurances panel on a subcontractor's page (round 41).
//
// RecordsSection's construction one kind over: the record is the FACT ("Apex
// holds public liability, policy 04-1234, expiring in March"), the certificate
// of currency is the scan filed against it, and a subbie with a policy and no
// scan still has the policy — still on the page, still reaching the reminders
// when it lapses. So records lead, and their certificates hang off them.
//
// NO LOOSE-FILE LIST HERE, where RecordsSection has one. A person's folders
// replaced their flat documents list, so the folder section is the only place
// a loose scan could show; a subcontractor keeps the flat Documents panel
// below this one, and everything unfiled — including a certificate whose
// record was deleted — is already visible there. A second listing would be
// the same file on one page twice.
import { useMemo, useState } from 'react';
import { ApiError } from '../../api';
import { contentUrl, documents, type Attachment } from '../../documentsApi';
import { insurances, type InsuranceRecord } from '../../insuranceApi';
import { formatDMY } from '../../lib/dates';
import { StatusBadge } from '../ui';
import { SortHeader } from '../ui/SortHeader';
import { sortRows, useSort } from '../../lib/sortRows';
import { DocumentViewer } from '../DocumentViewer';
import { formatSize } from '../Documents';
import { InsuranceModal } from './InsuranceModal';
import { DownloadLink } from '../DownloadLink';

type SortKey = 'title' | 'insurer' | 'policy' | 'expires';

export function InsurancesSection({
  resourceId,
  records,
  files,
  canEdit,
  onChanged,
  onError,
}: {
  resourceId: string;
  records: InsuranceRecord[];
  /** Every document on this resource; the certificates are found by link. */
  files: Attachment[];
  canEdit: boolean;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState<InsuranceRecord | 'new' | null>(null);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // By expiry: the question this panel exists to answer is what lapses next.
  const { sortKey, sortDir, onSort } = useSort<SortKey>('expires');

  const scansFor = (recordId: string) => files.filter((f) => f.insuranceRecordId === recordId);

  const sorted = useMemo(
    () =>
      sortRows(
        records,
        (record) => {
          switch (sortKey) {
            case 'title':
              return record.title;
            case 'insurer':
              return record.insurer;
            case 'policy':
              return record.policyNumber;
            case 'expires':
              return record.expiresOn;
          }
        },
        sortDir
      ),
    [records, sortKey, sortDir]
  );

  async function remove(record: InsuranceRecord) {
    if (
      !window.confirm(
        `Remove “${record.title}”? Any certificates filed against it stay in Documents.`
      )
    ) {
      return;
    }
    setBusy(record.id);
    try {
      await insurances.remove(resourceId, record.id);
      onChanged('Removed.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(null);
    }
  }

  async function unlink(file: Attachment) {
    try {
      await documents.update(file.id, { insuranceRecordId: null });
      onChanged('Unlinked. The file is still under Documents.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not unlink that.');
    }
  }

  /** The certificates hanging off one record — shared by the card and the
      table (RecordsSection's idiom, click-stopping included). */
  const scanLinks = (record: InsuranceRecord) => {
    const scans = scansFor(record.id);
    if (scans.length === 0) return null;
    return (
      <ul className="m-0 mt-1 list-none space-y-0.5 p-0 text-sm">
        {scans.map((file) => (
          <li key={file.id} className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="table-link inline-flex min-h-11 items-center text-left sm:min-h-0"
              onClick={(event) => {
                event.stopPropagation();
                setViewing(file);
              }}
            >
              📎 {file.title}
            </button>
            <span className="text-xs text-gray-500">{formatSize(file.sizeBytes)}</span>
            <DownloadLink
              href={contentUrl(file.id, { download: true })}
              className="row-action"
              onClick={(event) => event.stopPropagation()}
            >
              Download
            </DownloadLink>
            {canEdit && (
              <button
                type="button"
                className="row-action"
                onClick={(event) => {
                  event.stopPropagation();
                  void unlink(file);
                }}
              >
                Unlink
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  /** "No. PL-0447215 · CGU · Expires 06/10/2026" — the card's detail line. */
  const detailLine = (record: InsuranceRecord) =>
    [
      record.policyNumber && `No. ${record.policyNumber}`,
      record.insurer,
      `Expires ${formatDMY(record.expiresOn)}`,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <section className="panel">
      <div className="row mb-4 border-b border-gray-200 pb-3">
        <h2 className="!mb-0">
          Insurances
          {records.length > 0 && <span className="muted"> ({records.length})</span>}
        </h2>
        <span className="spacer" />
        {canEdit && (
          <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing('new')}>
            Add insurance
          </button>
        )}
      </div>

      {records.length === 0 ? (
        <p className="muted">
          Nothing on file yet. Record each policy off the certificate of currency; the expiry
          shows up in Notifications before it lapses.
        </p>
      ) : (
        <>
          {/* Phones keep the stacked cards; the table below is the desktop
              half of the same list (phase 8's DM3 pair). */}
          <ul className="m-0 list-none space-y-3 p-0 sm:hidden">
            {sorted.map((record) => (
              <li key={record.id} className="border-t border-gray-200 pt-3 first:border-0 first:pt-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{record.title}</span>
                      {/* The fixed 30-day chip, deliberately not the company's
                          reminder window (spec 03). */}
                      <StatusBadge kind="expiry" status={record.expiry} />
                    </div>

                    <div className="mt-0.5 text-sm text-gray-600">{detailLine(record)}</div>

                    {record.notes && (
                      <div className="mt-0.5 text-sm text-gray-600">{record.notes}</div>
                    )}

                    {scanLinks(record)}
                  </div>

                  {canEdit && (
                    <div className="flex flex-none flex-wrap justify-end gap-1">
                      <button type="button" className="row-action" onClick={() => setEditing(record)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="row-action-danger"
                        disabled={busy === record.id}
                        onClick={() => void remove(record)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader
                    label="Insurance"
                    sortKey="title"
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  <SortHeader
                    label="Insurer"
                    sortKey="insurer"
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  <SortHeader
                    label="Policy no."
                    sortKey="policy"
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  <SortHeader
                    label="Expires"
                    sortKey="expires"
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                  />
                  {canEdit && (
                    <th scope="col" className="table-header w-px">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sorted.map((record) => (
                  <tr
                    key={record.id}
                    // The whole row opens the record (LinkedRow's rule; not a
                    // LinkedRow itself because the destination is a dialog).
                    className={canEdit ? 'cursor-pointer hover:bg-gray-50' : undefined}
                    onClick={canEdit ? () => setEditing(record) : undefined}
                  >
                    <td className="table-cell font-medium">
                      <span>{record.title}</span>
                      {record.notes && (
                        <div className="mt-0.5 text-xs font-normal text-gray-600">{record.notes}</div>
                      )}
                      {scanLinks(record)}
                    </td>
                    <td className="table-cell table-muted">{record.insurer || '—'}</td>
                    <td className="table-cell table-muted whitespace-nowrap">
                      {record.policyNumber || '—'}
                    </td>
                    <td className="table-cell whitespace-nowrap">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="table-muted">{formatDMY(record.expiresOn)}</span>
                        <StatusBadge kind="expiry" status={record.expiry} />
                      </span>
                    </td>
                    {canEdit && (
                      <td className="table-cell w-px whitespace-nowrap text-right">
                        <button
                          type="button"
                          className="row-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditing(record);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="row-action-danger"
                          disabled={busy === record.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            void remove(record);
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <InsuranceModal
          resourceId={resourceId}
          record={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            onChanged(message);
          }}
        />
      )}

      {viewing && <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />}
    </section>
  );
}
